import { useCallback, useMemo, useRef, useState } from "react";
import { Badge, Form } from "react-bootstrap";
import { DropdownItem, DropdownPortal } from "./DropdownPortal";

/**
 * Props for the ControlTags component.
 */
export interface ControlTagsProps {
  /**
   * List of available tag options.
   */
  availableOptions: string[];
  /**
   * Currently selected tags.
   */
  selectedTags: string[];
  /**
   * Callback invoked when the selected tags change.
   *
   * @param selectedTags - The selected tags.
   */
  onSelectionChange: (selectedTags: string[]) => void;
  /**
   * Placeholder text for the input.
   */
  placeholder?: string;
  /**
   * Whether the control is disabled.
   */
  disabled?: boolean;
  /**
   * Whether creating new tags is allowed.
   */
  allowCreate?: boolean;
  /**
   * Optional custom CSS class.
   */
  className?: string;
}

/**
 * A typeahead control for selecting multiple tags from a list of options with support for filtering and creating new tags.
 * Features include autocomplete dropdown, keyboard navigation, and optional tag creation.
 *
 * @param props - Component props.
 * @param props.availableOptions - List of available tag options.
 * @param props.selectedTags - Currently selected tags.
 * @param props.onSelectionChange - Callback when selection changes.
 * @param props.placeholder - Placeholder text for the input (defaults to "Type to search...").
 * @param props.disabled - Whether the control is disabled (defaults to false).
 * @param props.allowCreate - Whether to allow creating new tags not in availableOptions (defaults to false).
 * @param props.className - Custom CSS class.
 * @returns A React functional component rendering a tag input with dropdown.
 */
export const ControlTags = ({
  availableOptions,
  selectedTags,
  onSelectionChange,
  placeholder = "Type to search...",
  disabled = false,
  allowCreate = false,
  className,
}: ControlTagsProps) => {
  const [searchText, setSearchText] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  /**
   * Filter available options based on search text and exclude already selected.
   */
  const dropdownItems = useMemo((): DropdownItem[] => {
    const filtered = availableOptions.filter(
      (option) =>
        option.toLowerCase().includes(searchText.toLowerCase()) && !selectedTags.includes(option),
    );

    const items: DropdownItem[] = filtered.map((option, index) => ({
      id: `option-${index}`,
      text: option,
      data: option,
    }));

    // Add create option if allowCreate is true and search text doesn't match any existing option
    if (
      allowCreate &&
      searchText.trim() &&
      !availableOptions.some((opt) => opt.toLowerCase() === searchText.toLowerCase()) &&
      !selectedTags.includes(searchText.trim())
    ) {
      items.unshift({
        id: "create-option",
        text: `Create: "${searchText.trim()}"`,
        data: searchText.trim(),
        isCreateOption: true,
      });
    }

    return items;
  }, [availableOptions, selectedTags, searchText, allowCreate]);

  /**
   * Handle adding a tag.
   */
  const handleAddTag = useCallback(
    (item: DropdownItem) => {
      const tagToAdd = item.isCreateOption ? (item.data as string) : item.text;

      /* istanbul ignore else */
      if (!selectedTags.includes(tagToAdd)) {
        onSelectionChange([...selectedTags, tagToAdd]);
        setSearchText("");
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Handle removing a tag.
   */
  const handleRemoveTag = useCallback(
    (tagToRemove: string) => {
      onSelectionChange(selectedTags.filter((tag) => tag !== tagToRemove));
    },
    [selectedTags, onSelectionChange],
  );

  /**
   * Handle input changes.
   */
  const handleInputChange = useCallback((value: string) => {
    setSearchText(value);
    setShowDropdown(value.trim().length > 0);
    setSelectedIndex(-1);
  }, []);

  /**
   * Handle input focus.
   */
  const handleInputFocus = useCallback(() => {
    setShowDropdown(searchText.trim().length > 0 || dropdownItems.length > 0);
  }, [searchText, dropdownItems.length]);

  /**
   * Handle input blur with delay to allow for dropdown clicks.
   */
  const handleInputBlur = useCallback(() => {
    // Delay hiding dropdown to allow for clicks
    setShowDropdown(false);
    setSelectedIndex(-1);
  }, []);

  /**
   * Handle key press events.
   */
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && dropdownItems.length > 0) {
        e.preventDefault();

        if (selectedIndex < dropdownItems.length && selectedIndex >= 0) {
          handleAddTag(dropdownItems[selectedIndex]);
        }
      } else if (e.key === "Escape") {
        setShowDropdown(false);
        setSearchText("");
        setSelectedIndex(-1);
      }
    },
    [dropdownItems, handleAddTag, selectedIndex],
  );

  return (
    <div className={`tag-typeahead ${className ?? ""}`}>
      {/* Single line layout with tags first, then input */}
      <div className="d-flex flex-wrap align-items-center gap-1">
        {/* Selected Tags Display */}
        {selectedTags.map((tag) => (
          <Badge
            key={tag}
            bg="secondary"
            className="d-flex align-items-center"
            style={{
              cursor: disabled ? "default" : "pointer",
              fontSize: "1.1em",
            }}
          >
            <span>{tag}</span>
            {!disabled && (
              <button
                type="button"
                className="btn-close btn-close-white ms-1"
                style={{ fontSize: "0.6em" }}
                onClick={() => handleRemoveTag(tag)}
                aria-label={`Remove ${tag}`}
              />
            )}
          </Badge>
        ))}

        {/* Search Input */}
        <div
          className="position-relative flex-grow-1"
          style={{ minWidth: "200px" }}
        >
          <Form.Control
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={searchText}
            onChange={(e) => handleInputChange(e.target.value)}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyDown={handleKeyPress}
            disabled={disabled}
            autoComplete="off"
          />
          <DropdownPortal
            isOpen={showDropdown && dropdownItems.length > 0 && !disabled}
            items={dropdownItems}
            onSelect={handleAddTag}
            onClose={() => {
              setShowDropdown(false);
              setSelectedIndex(-1);
            }}
            anchorRef={inputRef}
            selectedIndex={selectedIndex}
            onSelectedIndexChange={setSelectedIndex}
          />
        </div>
      </div>
    </div>
  );
};

ControlTags.displayName = "ControlTags";
